import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Plus, Edit, Trash2, Save, X, Coins, Recycle, Star, Flame } from "lucide-react";

const RARITIES = ["common", "uncommon", "rare", "epic", "legendary"];

const RARITY_COLOR = {
  common: "text-slate-300",
  uncommon: "text-emerald-300",
  rare: "text-sky-300",
  epic: "text-purple-300",
  legendary: "text-amber-300",
};

export default function ShopPacksTab({ token, API }) {
  const [cardPacks, setCardPacks] = useState([]);
  const [dustPacks, setDustPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal / Form states
  const [editingCardPack, setEditingCardPack] = useState(null);
  const [editingDustPack, setEditingDustPack] = useState(null);
  const [showAddCardPack, setShowAddCardPack] = useState(false);
  const [showAddDustPack, setShowAddDustPack] = useState(false);

  // Form states
  const [cardPackForm, setCardPackForm] = useState(null);
  const [dustPackForm, setDustPackForm] = useState(null);

  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cpRes, dpRes] = await Promise.all([
        axios.get(`${API}/admin/card-packs`, { headers: getHeaders() }),
        axios.get(`${API}/admin/dust-packs`, { headers: getHeaders() })
      ]);
      setCardPacks(cpRes.data);
      setDustPacks(dpRes.data);
    } catch (e) {
      toast.error("Failed to load shop packs configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, token]);

  // Handle Card Pack Form Actions
  const initCardPackAdd = () => {
    setCardPackForm({
      id: "",
      name_en: "",
      name_it: "",
      cost_dust: 0,
      cost_gold: 0,
      cards: 3,
      guaranteed_min_rarity: "rare",
      foil_chance: 0.15,
      guaranteed_foils: 0,
      order: cardPacks.length + 1,
      rarity_weights: {
        common: 0,
        uncommon: 50,
        rare: 35,
        epic: 15,
        legendary: 0
      }
    });
    setEditingCardPack(null);
    setShowAddCardPack(true);
  };

  const initCardPackEdit = (pack) => {
    setCardPackForm({
      ...pack,
      rarity_weights: {
        common: pack.rarity_weights?.common ?? 0,
        uncommon: pack.rarity_weights?.uncommon ?? 0,
        rare: pack.rarity_weights?.rare ?? 0,
        epic: pack.rarity_weights?.epic ?? 0,
        legendary: pack.rarity_weights?.legendary ?? 0
      }
    });
    setEditingCardPack(pack.id);
    setShowAddCardPack(true);
  };

  const handleCardPackSubmit = async (e) => {
    e.preventDefault();
    if (!cardPackForm.id.trim()) {
      toast.error("Pack ID is required");
      return;
    }
    if (!cardPackForm.name_en.trim() || !cardPackForm.name_it.trim()) {
      toast.error("Name translations (EN & IT) are required");
      return;
    }

    try {
      const payload = {
        ...cardPackForm,
        id: cardPackForm.id.toLowerCase().replace(/\s+/g, "-"),
        cost_dust: parseInt(cardPackForm.cost_dust) || 0,
        cost_gold: parseInt(cardPackForm.cost_gold) || 0,
        cards: parseInt(cardPackForm.cards) || 1,
        guaranteed_foils: parseInt(cardPackForm.guaranteed_foils) || 0,
        foil_chance: parseFloat(cardPackForm.foil_chance),
        order: parseInt(cardPackForm.order) || 0,
        rarity_weights: Object.fromEntries(
          Object.entries(cardPackForm.rarity_weights).map(([k, v]) => [k, parseInt(v) || 0])
        )
      };

      if (payload.foil_chance < 0 || payload.foil_chance > 1) {
        toast.error("Foil chance must be between 0.0 and 1.0");
        return;
      }
      if (payload.guaranteed_foils > payload.cards) {
        toast.error("Guaranteed foils cannot be greater than cards count");
        return;
      }

      if (editingCardPack) {
        await axios.put(`${API}/admin/card-packs/${editingCardPack}`, payload, { headers: getHeaders() });
        toast.success("Card pack updated successfully");
      } else {
        await axios.post(`${API}/admin/card-packs`, payload, { headers: getHeaders() });
        toast.success("Card pack created successfully");
      }
      setShowAddCardPack(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const handleCardPackDelete = async (packId) => {
    if (!window.confirm("Are you sure you want to delete this card pack? players who purchased this pack will still keep their cards, but won't see this pack in the shop anymore.")) return;
    try {
      await axios.delete(`${API}/admin/card-packs/${packId}`, { headers: getHeaders() });
      toast.success("Card pack deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete card pack");
    }
  };

  // Handle Dust Pack Form Actions
  const initDustPackAdd = () => {
    setDustPackForm({
      id: "",
      gold_cost: 100,
      dust_gained: 250,
      rate: 2.5,
      name_en: "",
      name_it: "",
      label_badge_en: "",
      label_badge_it: "",
      order: dustPacks.length + 1,
      best_value: false
    });
    setEditingDustPack(null);
    setShowAddDustPack(true);
  };

  const initDustPackEdit = (pack) => {
    setDustPackForm({ ...pack });
    setEditingDustPack(pack.id);
    setShowAddDustPack(true);
  };

  const handleDustPackSubmit = async (e) => {
    e.preventDefault();
    if (!dustPackForm.id.trim()) {
      toast.error("Dust pack ID is required");
      return;
    }
    if (!dustPackForm.name_en.trim() || !dustPackForm.name_it.trim()) {
      toast.error("Name translations (EN & IT) are required");
      return;
    }

    try {
      const gold_cost = parseInt(dustPackForm.gold_cost) || 1;
      const dust_gained = parseInt(dustPackForm.dust_gained) || 0;
      const rate = parseFloat((dust_gained / gold_cost).toFixed(2)) || 0;

      const payload = {
        ...dustPackForm,
        id: dustPackForm.id.toLowerCase().replace(/\s+/g, "-"),
        gold_cost,
        dust_gained,
        rate,
        order: parseInt(dustPackForm.order) || 0
      };

      if (editingDustPack) {
        await axios.put(`${API}/admin/dust-packs/${editingDustPack}`, payload, { headers: getHeaders() });
        toast.success("Dust pack updated successfully");
      } else {
        await axios.post(`${API}/admin/dust-packs`, payload, { headers: getHeaders() });
        toast.success("Dust pack created successfully");
      }
      setShowAddDustPack(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const handleDustPackDelete = async (packId) => {
    if (!window.confirm("Are you sure you want to delete this dust pouch exchange?")) return;
    try {
      await axios.delete(`${API}/admin/dust-packs/${packId}`, { headers: getHeaders() });
      toast.success("Dust pack deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete dust pack");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-[#94a3b8]">
        <Loader2 className="animate-spin mr-2" size={20} /> Loading packs configuration...
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* 1. CARD PACKS SECTION */}
      <section className="bg-[#131316] border border-[#cfb53b]/10 rounded-xl p-5 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-heading text-lg text-[#cfb53b] flex items-center gap-2">
              <Star size={18} className="text-[#cfb53b]" /> Card Packs (Bustine)
            </h2>
            <p className="text-xs text-[#94a3b8] mt-1">
              Configure purchasable card packs. You can define exact card counts, min guaranteed rarity, and customized rarity weights.
            </p>
          </div>
          <Button
            onClick={initCardPackAdd}
            className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading text-xs px-3 py-1.5 h-auto flex items-center gap-1"
          >
            <Plus size={14} /> Add Card Pack
          </Button>
        </div>

        {/* Card Packs List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cardPacks.map((pack) => (
            <div
              key={pack.id}
              className="bg-[#1a1a1d] border border-[#2d3748] rounded-lg p-4 space-y-3 relative flex flex-col justify-between"
            >
              <div>
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <h3 className="font-heading text-sm text-[#e2e8f0]">{pack.name_en}</h3>
                    <p className="text-[10px] text-[#94a3b8] italic">{pack.name_it}</p>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#2d3748] text-[#94a3b8] font-mono mt-1 inline-block">
                      ID: {pack.id}
                    </span>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-[11px]">
                    {pack.cost_gold > 0 && (
                      <span className="text-amber-400 flex items-center gap-0.5 font-bold">
                        <Coins size={12} /> {pack.cost_gold}
                      </span>
                    )}
                    {pack.cost_dust > 0 && (
                      <span className="text-sky-300 flex items-center gap-0.5 font-bold">
                        <Recycle size={12} /> {pack.cost_dust}
                      </span>
                    )}
                    {pack.cost_gold === 0 && pack.cost_dust === 0 && (
                      <span className="text-green-400 font-bold">FREE</span>
                    )}
                  </div>
                </div>

                <div className="border-t border-[#2d3748]/50 my-3 pt-3 grid grid-cols-2 gap-y-2 text-xs">
                  <div>
                    <span className="text-[#94a3b8] block text-[9px] uppercase tracking-wider">Cards Count</span>
                    <span className="font-medium text-[#e2e8f0]">{pack.cards}</span>
                  </div>
                  <div>
                    <span className="text-[#94a3b8] block text-[9px] uppercase tracking-wider">Guaranteed Min</span>
                    <span className={`font-medium capitalize ${RARITY_COLOR[pack.guaranteed_min_rarity]}`}>
                      {pack.guaranteed_min_rarity}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#94a3b8] block text-[9px] uppercase tracking-wider">Foil Chance</span>
                    <span className="font-medium text-purple-300">{(pack.foil_chance * 100).toFixed(0)}%</span>
                  </div>
                  <div>
                    <span className="text-[#94a3b8] block text-[9px] uppercase tracking-wider">Guaranteed Foils</span>
                    <span className="font-medium text-[#e2e8f0]">{pack.guaranteed_foils}</span>
                  </div>
                </div>

                <div className="bg-[#0a0a0c] rounded p-2 mt-2">
                  <span className="text-[#94a3b8] block text-[9px] uppercase tracking-wider mb-1">Rarity Weights</span>
                  <div className="flex justify-between text-[10px]">
                    {RARITIES.map((r) => (
                      <div key={r} className="text-center">
                        <div className={`${RARITY_COLOR[r]} font-mono capitalize`}>{r[0].toUpperCase()}</div>
                        <div className="text-[#e2e8f0] font-bold mt-0.5">
                          {pack.rarity_weights?.[r] ?? 0}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-3 mt-3 border-t border-[#2d3748]/30">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => initCardPackEdit(pack)}
                  className="h-7 border-[#2d3748] text-[#94a3b8] hover:text-[#cfb53b] text-[11px] px-2.5"
                >
                  <Edit size={12} className="mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCardPackDelete(pack.id)}
                  className="h-7 text-[#8b0000] hover:text-red-400 hover:bg-red-950/20 text-[11px] px-2.5"
                >
                  <Trash2 size={12} className="mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* 1.1 ADD/EDIT CARD PACK MODAL */}
        {showAddCardPack && cardPackForm && (
          <div className="fixed inset-0 z-50 bg-[#0a0a0c]/80 flex items-center justify-center p-4">
            <div className="bg-[#131316] border border-[#cfb53b]/20 rounded-xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto space-y-4">
              <div className="flex justify-between items-center pb-2 border-b border-[#2d3748]">
                <h3 className="font-heading text-base text-[#cfb53b]">
                  {editingCardPack ? `Edit Card Pack: ${cardPackForm.id}` : "Create New Card Pack"}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddCardPack(false)}
                  className="text-[#94a3b8] h-8 w-8 p-0"
                >
                  <X size={16} />
                </Button>
              </div>

              <form onSubmit={handleCardPackSubmit} className="space-y-4 text-xs">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Pack Unique ID</Label>
                    <Input
                      type="text"
                      disabled={!!editingCardPack}
                      value={cardPackForm.id}
                      onChange={(e) => setCardPackForm({ ...cardPackForm, id: e.target.value })}
                      placeholder="e.g. silver-v2"
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Display Order (Shop)</Label>
                    <Input
                      type="number"
                      value={cardPackForm.order}
                      onChange={(e) => setCardPackForm({ ...cardPackForm, order: e.target.value })}
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Name (EN)</Label>
                    <Input
                      type="text"
                      value={cardPackForm.name_en}
                      onChange={(e) => setCardPackForm({ ...cardPackForm, name_en: e.target.value })}
                      placeholder="Silver Pack"
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Name (IT)</Label>
                    <Input
                      type="text"
                      value={cardPackForm.name_it}
                      onChange={(e) => setCardPackForm({ ...cardPackForm, name_it: e.target.value })}
                      placeholder="Pacchetto Argenteo"
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Gold Cost</Label>
                    <Input
                      type="number"
                      value={cardPackForm.cost_gold}
                      onChange={(e) => setCardPackForm({ ...cardPackForm, cost_gold: e.target.value })}
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Dust Cost</Label>
                    <Input
                      type="number"
                      value={cardPackForm.cost_dust}
                      onChange={(e) => setCardPackForm({ ...cardPackForm, cost_dust: e.target.value })}
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Cards Count</Label>
                    <Input
                      type="number"
                      value={cardPackForm.cards}
                      onChange={(e) => setCardPackForm({ ...cardPackForm, cards: e.target.value })}
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Guaranteed Foils</Label>
                    <Input
                      type="number"
                      value={cardPackForm.guaranteed_foils}
                      onChange={(e) => setCardPackForm({ ...cardPackForm, guaranteed_foils: e.target.value })}
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Foil Chance (0.0 - 1.0)</Label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={cardPackForm.foil_chance}
                      onChange={(e) => setCardPackForm({ ...cardPackForm, foil_chance: e.target.value })}
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                </div>

                <div className="space-y-1 col-span-2">
                  <Label className="text-[#94a3b8]">Guaranteed Minimum Rarity</Label>
                  <select
                    value={cardPackForm.guaranteed_min_rarity}
                    onChange={(e) => setCardPackForm({ ...cardPackForm, guaranteed_min_rarity: e.target.value })}
                    className="w-full bg-[#0a0a0c] border border-[#2d3748] rounded px-3 h-8 text-xs text-[#e2e8f0]"
                  >
                    {RARITIES.map((r) => (
                      <option key={r} value={r}>
                        {r.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-[#0a0a0c] rounded-lg p-4 space-y-3">
                  <h4 className="font-heading text-[#cfb53b] text-xs">Rarity Weights (for random cards in pack)</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {RARITIES.map((r) => (
                      <div key={r} className="space-y-1">
                        <Label className={`text-[10px] ${RARITY_COLOR[r]} uppercase`}>{r}</Label>
                        <Input
                          type="number"
                          value={cardPackForm.rarity_weights[r]}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCardPackForm((prev) => ({
                              ...prev,
                              rarity_weights: {
                                ...prev.rarity_weights,
                                [r]: val
                              }
                            }));
                          }}
                          className="bg-[#1a1a1d] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#64748b] mt-1">
                    * Rarity weights do not need to sum to 100 — the backend automatically normalizes them.
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-[#2d3748]">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddCardPack(false)}
                    className="h-8 border-[#2d3748] text-[#94a3b8] hover:bg-[#1a1a1d]"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="h-8 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading"
                  >
                    <Save size={14} className="mr-1" /> {editingCardPack ? "Save Pack" : "Create Pack"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </section>

      {/* 2. DUST PACKS SECTION */}
      <section className="bg-[#131316] border border-[#cfb53b]/10 rounded-xl p-5 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-heading text-lg text-[#cfb53b] flex items-center gap-2">
              <Recycle size={18} className="text-sky-300" /> Dust pouch exchanges (Gold ➔ Dust)
            </h2>
            <p className="text-xs text-[#94a3b8] mt-1">
              Configure the dust pouches available for purchase with in-game Gold in the Shop. You can set badges (e.g. "Best Value" or "Popular").
            </p>
          </div>
          <Button
            onClick={initDustPackAdd}
            className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading text-xs px-3 py-1.5 h-auto flex items-center gap-1"
          >
            <Plus size={14} /> Add Dust Pack
          </Button>
        </div>

        {/* Dust Packs List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dustPacks.map((pack) => (
            <div
              key={pack.id}
              className="bg-[#1a1a1d] border border-[#2d3748] rounded-lg p-4 space-y-3 relative flex flex-col justify-between"
            >
              <div>
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <h3 className="font-heading text-sm text-[#e2e8f0] flex items-center gap-1.5">
                      {pack.name_en}
                      {pack.best_value && <Flame size={12} className="text-red-400" title="Best Value" />}
                    </h3>
                    <p className="text-[10px] text-[#94a3b8] italic">{pack.name_it}</p>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#2d3748] text-[#94a3b8] font-mono mt-1 inline-block">
                      ID: {pack.id}
                    </span>
                  </div>
                  <div className="text-right text-xs">
                    <span className="text-amber-400 font-bold block flex items-center justify-end gap-0.5">
                      <Coins size={12} /> {pack.gold_cost}
                    </span>
                    <span className="text-sky-300 font-bold block flex items-center justify-end gap-0.5 mt-0.5">
                      <Recycle size={12} /> {pack.dust_gained}
                    </span>
                  </div>
                </div>

                <div className="border-t border-[#2d3748]/50 my-3 pt-3 grid grid-cols-2 gap-y-2 text-xs">
                  <div>
                    <span className="text-[#94a3b8] block text-[9px] uppercase tracking-wider">Badge (EN)</span>
                    <span className="font-medium text-[#e2e8f0]">{pack.label_badge_en || "-"}</span>
                  </div>
                  <div>
                    <span className="text-[#94a3b8] block text-[9px] uppercase tracking-wider">Badge (IT)</span>
                    <span className="font-medium text-[#e2e8f0]">{pack.label_badge_it || "-"}</span>
                  </div>
                  <div>
                    <span className="text-[#94a3b8] block text-[9px] uppercase tracking-wider">Rate</span>
                    <span className="font-medium text-[#e2e8f0]">1g ➔ {pack.rate} dust</span>
                  </div>
                  <div>
                    <span className="text-[#94a3b8] block text-[9px] uppercase tracking-wider">Order</span>
                    <span className="font-medium text-[#e2e8f0]">{pack.order}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-3 mt-3 border-t border-[#2d3748]/30">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => initDustPackEdit(pack)}
                  className="h-7 border-[#2d3748] text-[#94a3b8] hover:text-[#cfb53b] text-[11px] px-2.5"
                >
                  <Edit size={12} className="mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDustPackDelete(pack.id)}
                  className="h-7 text-[#8b0000] hover:text-red-400 hover:bg-red-950/20 text-[11px] px-2.5"
                >
                  <Trash2 size={12} className="mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* 2.1 ADD/EDIT DUST PACK MODAL */}
        {showAddDustPack && dustPackForm && (
          <div className="fixed inset-0 z-50 bg-[#0a0a0c]/80 flex items-center justify-center p-4">
            <div className="bg-[#131316] border border-[#cfb53b]/20 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto space-y-4">
              <div className="flex justify-between items-center pb-2 border-b border-[#2d3748]">
                <h3 className="font-heading text-base text-[#cfb53b]">
                  {editingDustPack ? `Edit Dust Pack: ${dustPackForm.id}` : "Create New Dust Pack"}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddDustPack(false)}
                  className="text-[#94a3b8] h-8 w-8 p-0"
                >
                  <X size={16} />
                </Button>
              </div>

              <form onSubmit={handleDustPackSubmit} className="space-y-4 text-xs">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Unique ID</Label>
                    <Input
                      type="text"
                      disabled={!!editingDustPack}
                      value={dustPackForm.id}
                      onChange={(e) => setDustPackForm({ ...dustPackForm, id: e.target.value })}
                      placeholder="e.g. mega-dust"
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Display Order</Label>
                    <Input
                      type="number"
                      value={dustPackForm.order}
                      onChange={(e) => setDustPackForm({ ...dustPackForm, order: e.target.value })}
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Name (EN)</Label>
                    <Input
                      type="text"
                      value={dustPackForm.name_en}
                      onChange={(e) => setDustPackForm({ ...dustPackForm, name_en: e.target.value })}
                      placeholder="Small Dust Pouch"
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Name (IT)</Label>
                    <Input
                      type="text"
                      value={dustPackForm.name_it}
                      onChange={(e) => setDustPackForm({ ...dustPackForm, name_it: e.target.value })}
                      placeholder="Sacchetto di Polvere Piccolo"
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Gold Cost</Label>
                    <Input
                      type="number"
                      value={dustPackForm.gold_cost}
                      onChange={(e) => setDustPackForm({ ...dustPackForm, gold_cost: e.target.value })}
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Dust Gained</Label>
                    <Input
                      type="number"
                      value={dustPackForm.dust_gained}
                      onChange={(e) => setDustPackForm({ ...dustPackForm, dust_gained: e.target.value })}
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Badge (EN)</Label>
                    <Input
                      type="text"
                      value={dustPackForm.label_badge_en}
                      onChange={(e) => setDustPackForm({ ...dustPackForm, label_badge_en: e.target.value })}
                      placeholder="Popular"
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#94a3b8]">Badge (IT)</Label>
                    <Input
                      type="text"
                      value={dustPackForm.label_badge_it}
                      onChange={(e) => setDustPackForm({ ...dustPackForm, label_badge_it: e.target.value })}
                      placeholder="Popolare"
                      className="bg-[#0a0a0c] border-[#2d3748] h-8 text-xs text-[#e2e8f0]"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2 pt-2">
                  <input
                    type="checkbox"
                    id="best_value"
                    checked={dustPackForm.best_value}
                    onChange={(e) => setDustPackForm({ ...dustPackForm, best_value: e.target.checked })}
                    className="bg-[#0a0a0c] border-[#2d3748] rounded text-[#cfb53b] focus:ring-[#cfb53b]"
                  />
                  <Label htmlFor="best_value" className="text-[#e2e8f0] cursor-pointer">
                    Flag as Best Value (Red flame indicator)
                  </Label>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-[#2d3748]">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddDustPack(false)}
                    className="h-8 border-[#2d3748] text-[#94a3b8] hover:bg-[#1a1a1d]"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="h-8 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading"
                  >
                    <Save size={14} className="mr-1" /> {editingDustPack ? "Save Pack" : "Create Pack"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
